<template>
  <div class="quick-setup-page col-lg-12 mx-2">
    <ContentContainer>
      <template v-slot:content>
        <!-- Header -->
        <div class="qs-heading mb-4">
          <div class="qs-heading__icon">
            <i class="material-icons">auto_awesome</i>
          </div>
          <div class="flex-grow-1">
            <h5 class="qs-heading__title">Quick Setup <span class="text-gradient">powered by Ezer AI</span></h5>
            <p class="qs-heading__subtitle">
              Tell us the basics. Ezer AI will create the Activity, Skills, Workgroups, recurrence, and setup defaults for you.
            </p>
          </div>
          <div class="qs-progress-pill">
            {{ completedSteps }}/5 completed
          </div>
        </div>

        <div class="row g-3">
          <!-- Left: Setup form -->
          <div class="col-lg-8">
            <div class="qs-card">
              <!-- Section 1: Activity -->
              <section class="qs-section">
                <div class="qs-section__header">
                  <div>
                    <span class="qs-step-badge">1</span>
                    <span class="fw-bold">Activity / Service</span>
                  </div>
                  <span v-if="isActivityValid" class="badge bg-gradient-success">Ready</span>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Activity/Service Name</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="activity.name"
                      placeholder="e.g. Sunday Morning Service"
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Starts At</label>
                    <div class="d-flex gap-2">
                      <select class="form-select" v-model="activity.startHour">
                        <option v-for="h in hours" :key="h" :value="h">{{ h }}</option>
                      </select>
                      <select class="form-select" v-model="activity.startMinute">
                        <option v-for="m in minutes" :key="m" :value="m">{{ m }}</option>
                      </select>
                      <select class="form-select" v-model="activity.startMeridiem">
                        <option>AM</option>
                        <option>PM</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Duration</label>
                    <div class="input-group">
                      <input type="number" class="form-control" v-model.number="activity.duration" min="15" />
                      <span class="input-group-text">minutes</span>
                    </div>
                  </div>
                  <div class="col-md-8 mb-3">
                    <label class="form-label">How often does it happen?</label>
                    <select class="form-select" v-model="activity.recurrence">
                      <option v-for="option in recurrenceOptions" :key="option" :value="option">
                        {{ option }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Start Date dropdown (shown for weekly recurrence) -->
                <div v-if="startDateOptions.length > 0" class="row">
                  <div class="col-md-8 mb-3">
                    <label class="form-label">Start Date</label>
                    <select class="form-select" v-model="activity.recurStartDate">
                      <option v-for="opt in startDateOptions" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                      </option>
                    </select>
                    <p class="text-xs text-secondary mt-1 mb-0">
                      Scheduling will begin on this date.
                    </p>
                  </div>
                </div>

                <div v-if="activity.recurrence === 'custom'" class="mb-3">
                  <label class="form-label">Custom recurrence</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="activity.customRecurrence"
                    placeholder="e.g. first and third Tuesdays"
                  />
                  <p class="text-xs text-secondary mt-1 mb-0">
                    Ezer AI will use this description to help set up recurrence.
                  </p>
                </div>

                <div v-if="isActivityMatchReviewVisible" class="alert alert-warning mt-3 mb-0" role="alert">
                  <strong>Review similar Activities before continuing.</strong>
                  <p class="small mb-2">
                    These names may refer to the same service. Reuse an existing Activity, or confirm that you need a separate one.
                  </p>
                  <ul class="mb-2">
                    <li v-for="match in activityMatchCandidates" :key="match.activityid || match.id || match.activityname">
                      <span>{{ match.activityname || match.name }}</span>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-primary ms-2 mb-0"
                        :disabled="saving"
                        @click="reuseSimilarActivity(match)"
                      >
                        Reuse this Activity
                      </button>
                    </li>
                  </ul>
                  <div class="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary mb-0"
                      :disabled="saving"
                      @click="createSeparateActivity"
                    >
                      None match — create a separate Activity
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-link mb-0"
                      :disabled="saving"
                      @click="clearActivityMatchReview"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </section>

              <!-- Section 2: Workgroups -->
              <section class="qs-section" :class="{ 'qs-section--locked': !isActivityValid }">
                <div class="qs-section__header">
                  <div>
                    <span class="qs-step-badge">2</span>
                    <span class="fw-bold">Workgroups</span>
                  </div>
                  <span v-if="isWorkgroupsValid" class="badge bg-gradient-success">Ready</span>
                </div>
                <p class="text-sm text-secondary mb-2">
                  Add the volunteer roles you need. Ezer AI will create the skills automatically from these names.
                </p>
                <WorkgroupTable v-model="workgroups" />
                <div v-if="generatedSkills.length" class="mt-3">
                  <p class="text-xs text-secondary mb-1">Ezer AI will create or reuse these skills:</p>
                  <span v-for="skill in generatedSkills" :key="skill" class="badge bg-gradient-info me-1 mb-1">
                    {{ skill }}
                  </span>
                </div>
              </section>

              <!-- Section 3: Save base setup + Add Users -->
              <section class="qs-section" :class="{ 'qs-section--locked': !isWorkgroupsValid }">
                <div class="qs-section__header">
                  <div>
                    <span class="qs-step-badge">3</span>
                    <span class="fw-bold">Create setup and add volunteers</span>
                  </div>
                  <span v-if="setupCreated && usersAdded" class="badge bg-gradient-success">Ready</span>
                  <span v-else-if="setupCreated" class="badge bg-gradient-warning">Add volunteers</span>
                </div>
                <p class="text-sm text-secondary">
                  We'll create the Activity, Skills, Workgroups, and recurrence first. Then you must add volunteers before finalizing.
                </p>
                <div class="d-flex flex-wrap gap-2">
                  <button
                    class="btn btn-success mb-0"
                    :class="{ 'qs-pulse': isWorkgroupsValid && !setupCreated && !saving }"
                    :disabled="saving || !isWorkgroupsValid || setupCreated || isActivityMatchReviewVisible || skillMatchReview.length > 0"
                    @click="createBaseSetup"
                  >
                    <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                    {{ setupCreated ? 'Setup created' : 'Create Activity, Skills & Workgroups' }}
                  </button>
                  <button
                    v-if="setupCreated"
                    class="btn btn-outline-primary mb-0"
                    :class="{ 'qs-pulse': setupCreated && !usersAdded }"
                    @click="openAddUsersModal"
                  >
                    <i class="material-icons" style="font-size: 18px; vertical-align: middle">group_add</i>
                    Add Users/Volunteers
                  </button>
                  <button
                    v-if="setupCreated"
                    class="btn btn-outline-secondary mb-0"
                    :class="{ 'qs-pulse': setupCreated && users.length >= minVolunteers && !skillsHavePeople }"
                    @click="openManageSkillsModal"
                  >
                    <i class="material-icons" style="font-size: 18px; vertical-align: middle">psychology</i>
                    Manage Skills
                  </button>
                  <button
                    v-if="setupCreated"
                    class="btn btn-outline-secondary mb-0"
                    @click="openManageUsersModal"
                  >
                    <i class="material-icons" style="font-size: 18px; vertical-align: middle">manage_accounts</i>
                    Manage Users
                  </button>
                </div>

                <!-- Similar skill review -->
                <div v-if="skillMatchReview.length" class="alert alert-warning mt-3 mb-0" role="alert">
                  <strong>Reuse existing skills?</strong>
                  <p class="small mb-2">
                    Some skills look similar to ones you already have. Reusing an existing skill keeps its people assigned.
                  </p>
                  <div v-for="item in skillMatchReview" :key="item.normKey" class="mb-2">
                    <div class="small fw-bold">"{{ item.name }}" looks similar to:</div>
                    <div v-for="candidate in item.candidates" :key="candidate.id" class="form-check">
                      <input
                        :id="`skill-${item.normKey}-${candidate.id}`"
                        v-model="skillChoices[item.normKey]"
                        class="form-check-input"
                        type="radio"
                        :name="`skillchoice-${item.normKey}`"
                        :value="candidate.id"
                      />
                      <label class="form-check-label small" :for="`skill-${item.normKey}-${candidate.id}`">
                        Use existing skill "{{ candidate.name }}"
                      </label>
                    </div>
                    <div class="form-check">
                      <input
                        :id="`skill-${item.normKey}-new`"
                        v-model="skillChoices[item.normKey]"
                        class="form-check-input"
                        type="radio"
                        :name="`skillchoice-${item.normKey}`"
                        value="new"
                      />
                      <label class="form-check-label small" :for="`skill-${item.normKey}-new`">
                        Create a new skill "{{ item.name }}"
                      </label>
                    </div>
                  </div>
                  <div class="d-flex flex-wrap gap-2">
                    <button type="button" class="btn btn-sm btn-primary mb-0" :disabled="saving" @click="confirmSkillMatches">
                      Continue
                    </button>
                    <button type="button" class="btn btn-sm btn-link mb-0" :disabled="saving" @click="clearSkillMatchReview">
                      Cancel
                    </button>
                  </div>
                </div>

                <!-- Skills and people -->
                <div v-if="setupCreated" class="mt-3">
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <h6 class="mb-0 text-sm">Skills and assigned people</h6>
                    <button
                      type="button"
                      class="btn btn-sm btn-link mb-0 p-0"
                      :disabled="skillAssignmentsLoading"
                      @click="loadSkillAssignments"
                    >
                      <span v-if="skillAssignmentsLoading" class="spinner-border spinner-border-sm me-1"></span>
                      Refresh
                    </button>
                  </div>
                  <table class="table table-sm align-middle mb-0 qs-skill-table">
                    <thead>
                      <tr>
                        <th class="text-xs">Skill</th>
                        <th class="text-xs text-end">People in skill</th>
                        <th v-if="eligibilityLoaded" class="text-xs text-end">Can be scheduled for this Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!skillAssignments.length">
                        <td :colspan="eligibilityLoaded ? 3 : 2" class="text-sm text-secondary">No skills found for this Activity yet.</td>
                      </tr>
                      <tr v-for="skill in skillAssignments" :key="skill.skill_id">
                        <td class="text-sm">{{ skill.skill_name }}</td>
                        <td class="text-end">
                          <span class="badge" :class="skill.people_count > 0 ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                            {{ skill.people_count }}
                          </span>
                        </td>
                        <td v-if="eligibilityLoaded" class="text-end">
                          <span
                            v-if="eligibilityBySkill[skill.skill_id]"
                            class="badge"
                            :class="eligibilityBySkill[skill.skill_id].eligible_people === eligibilityBySkill[skill.skill_id].total_people ? 'bg-gradient-success' : 'bg-gradient-warning'"
                          >
                            {{ eligibilityBySkill[skill.skill_id].eligible_people }} of {{ eligibilityBySkill[skill.skill_id].total_people }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- People held back by their preferred Activities/Workgroups -->
                  <div v-if="blockedPeopleTotal > 0" class="alert alert-warning mt-3 mb-0" role="alert">
                    <strong>{{ blockedPeopleTotal }} {{ blockedPeopleTotal === 1 ? 'person' : 'people' }} in these skills would be skipped by the auto-scheduler</strong>
                    <p class="small mb-2 mt-1">
                      These volunteers have already chosen <em>preferred</em> Activities or Workgroups. When someone has
                      preferences, the auto-scheduler only schedules them for the things on their list, and
                      "{{ activity.name }}" is new so it isn't on anyone's list yet. Without a change, your new Activity
                      could end up with few or no scheduled people.
                    </p>
                    <ul class="small mb-2">
                      <li v-for="skill in skillEligibility.filter(s => (s.blocked_people || []).length)" :key="skill.skill_id">
                        <strong>{{ skill.skill_name }}:</strong>
                        {{ skill.blocked_people.slice(0, 5).map(p => p.name || p.email).join(', ') }}<template v-if="skill.blocked_people.length > 5"> and {{ skill.blocked_people.length - 5 }} more</template>
                      </li>
                    </ul>
                    <p class="small mb-2">
                      Adding "{{ activity.name }}" to their preferred list keeps their existing preferences and lets them be scheduled here too.
                      You can also choose exactly who to add in the Activity's <strong>Preferred Users</strong> tab.
                    </p>
                    <div class="d-flex flex-wrap gap-2">
                      <button type="button" class="btn btn-sm btn-primary mb-0" :disabled="addingPreferred" @click="addAllBlockedAsPreferred">
                        <span v-if="addingPreferred" class="spinner-border spinner-border-sm me-1"></span>
                        Add all {{ blockedPeopleTotal }} as preferred for this Activity
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="openPreferredModal">
                        Choose in the Preferred Users tab
                      </button>
                    </div>
                  </div>
                  <div v-else-if="eligibilityLoaded && skillsHavePeople && eligiblePeopleTotal > 0" class="small text-success mt-2">
                    <i class="material-icons" style="font-size: 14px; vertical-align: middle">check_circle</i>
                    Everyone in these skills can be scheduled for this Activity.
                  </div>
                </div>

                <div v-if="setupCreated && !usersAdded" class="alert alert-warning mt-3 mb-0 py-2">
                  <i class="material-icons" style="font-size: 16px; vertical-align: middle">info</i>
                  <span class="text-sm ms-1">
                    <template v-if="users.length < minVolunteers">
                      Add at least {{ minVolunteers }} users (you currently have {{ users.length }}).
                    </template>
                    <template v-if="!skillsHavePeople">
                      <template v-if="users.length < minVolunteers"> Then </template>
                      <template v-else>Next, </template>
                      use <strong>Manage Skills</strong> to assign at least one person to one of the skills above. Auto-scheduling can only schedule people who have a skill assigned.
                    </template>
                  </span>
                </div>
                <div v-else-if="setupCreated && usersAdded" class="alert alert-success mt-3 mb-0 py-2">
                  <i class="material-icons" style="font-size: 16px; vertical-align: middle">check_circle</i>
                  <span class="text-sm ms-1">
                    Great! You have people assigned to skills. Add more volunteers any time so more roles can be filled automatically.
                  </span>
                </div>
              </section>

              <!-- Section 4: Admins -->
              <section class="qs-section" :class="{ 'qs-section--locked': !usersAdded }">
                <div class="qs-section__header">
                  <div>
                    <span class="qs-step-badge">4</span>
                    <span class="fw-bold">Assign Admins</span>
                  </div>
                  <span v-if="adminsReady" class="badge bg-gradient-success">Ready</span>
                </div>
                <AdminAssignment
                  :users="users"
                  :workgroups="createdWorkgroups"
                  :current-user-id="currentUserId"
                  @update:admins="adminSelection = $event"
                />
              </section>

              <!-- Section 5: Review & Enable -->
              <section class="qs-section" :class="{ 'qs-section--locked': !adminsReady }">
                <div class="qs-section__header">
                  <div>
                    <span class="qs-step-badge">5</span>
                    <span class="fw-bold">Review & Enable</span>
                  </div>
                  <span v-if="completed" class="badge bg-gradient-success">Complete</span>
                </div>
                <div class="qs-summary">
                  <p><strong>Activity:</strong> {{ activity.name || 'Not set' }}</p>
                  <p><strong>Schedule:</strong> {{ activity.recurrence }} at {{ activity.startHour }}:{{ activity.startMinute }} {{ activity.startMeridiem }}</p>
                  <p><strong>Workgroups:</strong> {{ workgroups.filter(w => w.name).length }}</p>
                  <p><strong>Skills:</strong> {{ generatedSkills.join(', ') || 'None yet' }}</p>
                </div>
                <button
                  class="btn btn-primary mb-0"
                  :class="{ 'qs-pulse': adminsReady && !completed && !finishing }"
                  :disabled="finishing || !adminsReady || completed"
                  @click="finishSetup"
                >
                  <span v-if="finishing" class="spinner-border spinner-border-sm me-1"></span>
                  {{ finishing ? 'Finalizing...' : completed ? 'Setup Complete' : 'Enable Activity & Complete Setup' }}
                </button>
                <div v-if="completed" class="alert alert-success mt-3 mb-2" role="status">
                  <i class="material-icons" style="font-size: 18px; vertical-align: middle">celebration</i>
                  Quick Setup is complete and the Activity is enabled.
                </div>
                <div v-if="completed" class="qs-generation-status" aria-live="polite">
                  <h6 class="qs-generation-status__title">What happens next</h6>
                  <ol class="qs-generation-status__steps">
                    <li class="qs-generation-status__step qs-generation-status__step--done">
                      <i class="material-icons">check_circle</i>
                      <div>
                        <strong>Setup saved</strong>
                        <span>The Activity, Workgroups, and admin assignments are enabled.</span>
                      </div>
                    </li>
                    <li class="qs-generation-status__step" :class="{
                      'qs-generation-status__step--done': engineStatus === 'done',
                      'qs-generation-status__step--waiting': engineStatus === 'running',
                      'qs-generation-status__step--attention': engineStatus === 'error',
                    }">
                      <i class="material-icons">{{ engineStatus === 'done' ? 'check_circle' : engineStatus === 'error' ? 'warning' : 'auto_awesome' }}</i>
                      <div class="w-100">
                        <strong>{{ engineStatus === 'done' ? 'Your schedule is ready' : engineStatus === 'running' ? 'Building your schedule' : 'Generate your schedule now' }}</strong>
                        <span v-if="engineStatus === 'idle'">No need to wait for the background scheduler. Run the auto-scheduler now for this Activity only and watch it work.</span>
                        <span v-if="engineStatus === 'idle' && blockedPeopleTotal > 0" class="text-warning d-block">
                          Heads up: {{ blockedPeopleTotal }} {{ blockedPeopleTotal === 1 ? 'person is' : 'people are' }} still skipped because of their preferences (see step 3), so some roles may stay open.
                        </span>
                        <div v-if="engineStatus !== 'idle'" class="mt-2">
                          <div class="small mb-1">1. Creating schedule instances</div>
                          <div class="progress mb-2" style="height: 10px">
                            <div class="progress-bar bg-gradient-success" role="progressbar"
                              :class="{ 'progress-bar-striped progress-bar-animated': engineStatus === 'running' && engineStep === 1 }"
                              :style="{ width: (engineStep === 1 && engineStatus === 'running' ? 100 : engineInstancePct) + '%' }"
                              :aria-valuenow="engineInstancePct" aria-valuemin="0" aria-valuemax="100"></div>
                          </div>
                          <div class="small mb-1">
                            2. Scheduling people into roles
                            <span v-if="engineInstancesTotal">({{ engineInstancesScheduled }} of {{ engineInstancesTotal }} events)</span>
                          </div>
                          <div class="progress mb-2" style="height: 10px">
                            <div class="progress-bar bg-gradient-info" role="progressbar"
                              :class="{ 'progress-bar-striped progress-bar-animated': engineStatus === 'running' && engineStep === 2 }"
                              :style="{ width: engineSchedulePct + '%' }"
                              :aria-valuenow="engineSchedulePct" aria-valuemin="0" aria-valuemax="100"></div>
                          </div>
                          <div v-if="engineStatus === 'running'" class="small text-secondary">{{ engineCurrentLabel }}</div>
                          <div v-if="engineStatus === 'done'" class="small">
                            {{ engineInstancesTotal }} event(s) created and {{ enginePersonsAssigned }} volunteer assignment(s) made.
                            <span v-if="!engineInstancesTotal">No upcoming events fell inside the scheduling window yet.</span>
                          </div>
                          <div v-if="engineStatus === 'error'" class="small text-danger">{{ engineError }}</div>
                        </div>
                        <button v-if="engineStatus === 'idle' || engineStatus === 'error'" type="button"
                          class="btn btn-sm btn-primary mt-2 mb-0" @click="runEngineNow">
                          {{ engineStatus === 'error' ? 'Try again' : 'Generate my schedule now' }}
                        </button>
                        <button v-if="engineStatus === 'done'" type="button"
                          class="btn btn-sm btn-success mt-2 mb-0" @click="goToManageSchedules">
                          <i class="material-icons" style="font-size: 16px; vertical-align: middle">calendar_month</i>
                          View schedules in Manage Schedules
                        </button>
                      </div>
                    </li>
                    <li v-if="engineStatus !== 'done' && scheduleProgressStatus !== 'needs_schedule'" class="qs-generation-status__step" :class="{
                      'qs-generation-status__step--done': scheduleProgressStatus === 'ready',
                      'qs-generation-status__step--waiting': !['ready', 'error', 'timed_out'].includes(scheduleProgressStatus),
                      'qs-generation-status__step--attention': ['error', 'timed_out'].includes(scheduleProgressStatus),
                    }">
                      <i class="material-icons">{{ scheduleProgressStatus === 'ready' ? 'check_circle' : scheduleProgressStatus === 'error' || scheduleProgressStatus === 'timed_out' ? 'warning' : 'schedule' }}</i>
                      <div>
                        <strong>{{ scheduleProgressStatus === 'checking' ? 'Checking for upcoming schedule instances' : scheduleProgressStatus === 'ready' ? 'Upcoming schedule instances created' : scheduleProgressStatus === 'timed_out' ? 'Schedule generation is taking longer than expected' : scheduleProgressStatus === 'error' ? 'Schedule status could not be checked' : 'Waiting for background schedule generation' }}</strong>
                        <span v-if="scheduleProgressStatus === 'checking'">Checking whether the saved recurrence has generated upcoming instances...</span>
                        <span v-else-if="scheduleProgressStatus === 'ready'">{{ scheduleProgress.upcoming_instance_count }} upcoming schedule instance(s) were found. Automatic assignments may continue in the background; review staffing in Manage Schedules.</span>
                        <span v-else-if="scheduleProgressStatus === 'timed_out'">No upcoming instances were found after several checks. The Activity is still enabled; check Manage Schedules or try another status check.</span>
                        <span v-else-if="scheduleProgressStatus === 'error'">The Activity is still enabled, but the status request failed. You can check again.</span>
                        <span v-else>The scheduler runs separately from Quick Setup. This status checks saved schedule instances and refreshes automatically for up to 12 minutes.</span>
                        <button v-if="['error', 'timed_out'].includes(scheduleProgressStatus)" type="button"
                          class="btn btn-sm btn-outline-secondary mt-2 mb-0" @click="refreshScheduleProgress">
                          Check status
                        </button>
                      </div>
                    </li>
                    <li v-else-if="engineStatus !== 'done'" class="qs-generation-status__step qs-generation-status__step--attention">
                      <i class="material-icons">warning</i>
                      <div>
                        <strong>Schedule setup still needs attention</strong>
                        <span>Quick Setup did not save a schedule for this single-event or custom selection. Complete the event date or recurrence in Activity settings before expecting automatic schedule instances.</span>
                      </div>
                    </li>
                    <li class="qs-generation-status__step">
                      <i class="material-icons">groups</i>
                      <div>
                        <strong>Review staffing</strong>
                        <span>Some roles may remain open if no eligible volunteers are available. Volunteer reminders follow your Location's settings and are not necessarily sent immediately.</span>
                        <button type="button" class="btn btn-sm btn-outline-primary mt-2 mb-0" @click="goToManageSchedules">
                          Go to Manage Schedules
                        </button>
                      </div>
                    </li>
                  </ol>
                  <div class="qs-generation-status__email" role="status">
                    <span v-if="completionEmailStatus === 'sending'">Sending the Quick Setup summary to the email address configured for your account...</span>
                    <span v-else-if="completionEmailStatus === 'sent'">Quick Setup summary email sent to the email address configured for your account.</span>
                    <span v-else-if="completionEmailStatus === 'error'">Setup succeeded, but the summary email could not be sent. {{ completionEmailError }}</span>
                    <span v-else>Preparing the Quick Setup summary email...</span>
                    <button
                      v-if="completionEmailStatus === 'error'"
                      type="button"
                      class="btn btn-sm btn-outline-primary mb-0"
                      :disabled="completionEmailStatus === 'sending'"
                      @click="sendCompletionEmail"
                    >
                      Retry email
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <!-- Right: Ezer AI chat -->
          <div class="col-lg-4">
            <div class="qs-chat-wrap">
              <QuickSetupChat :current-step="currentStep" :user-name="userName" />
            </div>
          </div>
        </div>

        <!-- Add users modal -->
        <Modal
          v-if="showAddUsersModal"
          modalHeading="Add Users/Volunteers"
          :iframeUrl="addUsersUrl"
          size="xl"
          @close="closeAddUsersModal"
        />

        <!-- Manage users modal -->
        <Modal
          v-if="showManageUsersModal"
          modalHeading="Manage Users"
          :iframeUrl="manageUsersUrl"
          size="xl"
          @close="closeManageUsersModal"
        />

        <!-- Manage skills modal -->
        <Modal
          v-if="showManageSkillsModal"
          modalHeading="Manage Skills"
          :iframeUrl="manageSkillsUrl"
          size="xl"
          @close="closeManageSkillsModal"
        />

        <!-- Preferred users modal -->
        <Modal
          v-if="showPreferredModal"
          modalHeading="Preferred Users for this Activity"
          :iframeUrl="preferredUrl"
          size="xl"
          @close="closePreferredModal"
        />
      </template>
    </ContentContainer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import ContentContainer from '@/components/PageLayout/ContentContainer.vue'
import Modal from '@/components/Modal/Modal.vue'
import WorkgroupTable from '@/components/quicksetup/WorkgroupTable.vue'
import AdminAssignment from '@/components/quicksetup/AdminAssignment.vue'
import QuickSetupChat from '@/components/quicksetup/QuickSetupChat.vue'
import { baseUrl } from '@/utils/BaseUrl'
import {
  parseSignUpContext,
  fetchLocationSignUpContext,
  fetchExistingSkills,
  fetchExistingActivities,
  fetchLocationUsers,
  createActivity,
  createSkill,
  createWorkgroup,
  createRecurrence,
  mapRecurrenceToApi,
  getRecurrenceStartDates,
  buildSkillMap,
  deriveSkillName,
  findMatchingSkill,
  findPotentialActivityMatches,
  normalizeActivityName,
  normalizeForComparison,
  saveActivityAdmins,
  saveWorkgroupAdmins,
  enableActivity,
  sendQuickSetupCompletionEmail,
  fetchQuickSetupScheduleProgress,
  fetchActivityWorkgroups,
  findSimilarSkills,
  fetchQuickSetupSkillAssignments,
  runQuickSetupEngine,
  fetchQuickSetupSkillEligibility,
  addQuickSetupPreferredPeople,
} from '@/services/QuickSetupService'

const store = useStore()
const router = useRouter()
const toast = useToast()

const session = computed(() => store.getters.stSessionData || {})
const locationId = computed(() => session.value.locationid)
const currentUserId = computed(() => session.value.personId || session.value.personid)
const userName = computed(() => session.value.firstname || '')

const activity = ref({
  name: '',
  description: 'Created via Quick Setup powered by Ezer AI',
  startHour: '11',
  startMinute: '00',
  startMeridiem: 'AM',
  duration: 90,
  recurrence: 'every Sunday',
  customRecurrence: '',
  recurStartDate: '',
})

const workgroups = ref([{ name: '', count: 1, autoSchedule: true }])
const users = ref([])
const existingSkills = ref([])
const createdActivityId = ref(null)
const createdSkillByName = ref({})
const createdWorkgroups = ref([])
const adminSelection = ref({ activityAdmins: [], workgroupAdmins: {} })
const saving = ref(false)
const finishing = ref(false)
const setupCreated = ref(false)
const completed = ref(false)
const completionEmailStatus = ref('idle')
const completionEmailError = ref('')
const scheduleProgressStatus = ref('idle')
const scheduleProgress = ref({ upcoming_instance_count: 0 })
const showAddUsersModal = ref(false)
const showManageUsersModal = ref(false)
const activityMatchCandidates = ref([])
const activityMatchQuery = ref('')
const showManageSkillsModal = ref(false)
const showPreferredModal = ref(false)
const skillEligibility = ref([])
const eligibilityLoaded = ref(false)
const addingPreferred = ref(false)
const skillAssignments = ref([])
const skillAssignmentsLoading = ref(false)
const skillMatchReview = ref([])
const skillChoices = ref({})
const skillReviewConfirmed = ref(false)
const engineStatus = ref('idle') // idle | running | done | error
const engineError = ref('')
const engineStep = ref(0) // 0 not started, 1 creating instances, 2 scheduling, 3 finished
const engineInstancesTotal = ref(0)
const engineInstancesScheduled = ref(0)
const enginePersonsAssigned = ref(0)
const engineCurrentLabel = ref('')
const minVolunteers = 2 // minimum total users (including admin) before finalizing
const MAX_PROGRESS_CHECKS = 24
const PROGRESS_CHECK_INTERVAL_MS = 30_000
let scheduleProgressTimer = null
let scheduleProgressCheckCount = 0
let scheduleProgressRequestInFlight = false

const recurrenceOptions = [
  'every Sunday',
  'every Monday',
  'every Tuesday',
  'every Wednesday',
  'every Thursday',
  'every Friday',
  'every Saturday',
  'single event',
  'custom',
]
const hours = ['01','02','03','04','05','06','07','08','09','10','11','12']
const minutes = ['00','15','30','45']

// Recurrence start date dropdown options
const recurrenceDayChoices = {
  'every Sunday': 'Sunday', 'every Monday': 'Monday', 'every Tuesday': 'Tuesday',
  'every Wednesday': 'Wednesday', 'every Thursday': 'Thursday',
  'every Friday': 'Friday', 'every Saturday': 'Saturday',
}

const startDateOptions = computed(() => {
  const day = recurrenceDayChoices[activity.value.recurrence]
  if (!day) return []
  return getRecurrenceStartDates(day, 3)
})

// Auto-select 2nd date option when recurrence changes
watch(() => activity.value.recurrence, () => {
  const opts = startDateOptions.value
  if (opts.length >= 2) {
    activity.value.recurStartDate = opts[1].value
  } else if (opts.length === 1) {
    activity.value.recurStartDate = opts[0].value
  } else {
    activity.value.recurStartDate = ''
  }
}, { immediate: true })

const isActivityValid = computed(() => {
  if (!activity.value.name?.trim()) return false
  if (activity.value.recurrence === 'custom' && !activity.value.customRecurrence?.trim()) return false
  // Weekly recurrence requires a start date
  if (recurrenceDayChoices[activity.value.recurrence] && !activity.value.recurStartDate) return false
  return true
})

const isWorkgroupsValid = computed(() => {
  return isActivityValid.value && workgroups.value.some(w => w.name?.trim() && Number(w.count) > 0)
})
const isActivityMatchReviewVisible = computed(() => (
  activityMatchCandidates.value.length > 0
  && activityMatchQuery.value === normalizeActivityName(activity.value.name)
))

const skillsHavePeople = computed(() => skillAssignments.value.some(s => Number(s.people_count) > 0))

const eligibilityBySkill = computed(() => Object.fromEntries(
  skillEligibility.value.map(s => [s.skill_id, s])
))
const blockedPeopleTotal = computed(() => {
  const ids = new Set()
  skillEligibility.value.forEach(s => (s.blocked_people || []).forEach(p => ids.add(p.person_id)))
  return ids.size
})
const eligiblePeopleTotal = computed(() => skillEligibility.value.reduce((sum, s) => sum + Number(s.eligible_people || 0), 0))
const preferredUrl = computed(() => (
  `/activities/edit/${locationId.value}/${createdActivityId.value}?layout=none&tab=preferred-users`
))

const usersAdded = computed(() => {
  return setupCreated.value && users.value.length >= minVolunteers && skillsHavePeople.value
})

const engineInstancePct = computed(() => (engineStep.value >= 2 ? 100 : 0))
const engineSchedulePct = computed(() => {
  if (engineStep.value >= 3) return 100
  if (!engineInstancesTotal.value) return 0
  return Math.round((engineInstancesScheduled.value / engineInstancesTotal.value) * 100)
})
const manageSchedulesPath = computed(() => `/all-schedules/${locationId.value}`)

const adminsReady = computed(() => {
  return usersAdded.value && (adminSelection.value.activityAdmins || []).length > 0
})

const generatedSkills = computed(() => {
  const names = workgroups.value
    .filter(w => w.name?.trim())
    .map(w => w.name.trim())
  const map = buildSkillMap(names)
  return Object.values(map).map(v => v.displayName)
})

const currentStep = computed(() => {
  if (!isActivityValid.value) return 'activity'
  if (!isWorkgroupsValid.value) return 'workgroups'
  if (!setupCreated.value) return 'users'
  if (!adminsReady.value) return 'admins'
  return 'review'
})

const completedSteps = computed(() => {
  let count = 0
  if (isActivityValid.value) count++
  if (isWorkgroupsValid.value) count++
  if (usersAdded.value) count++
  if (adminsReady.value) count++
  if (completed.value) count++
  return count
})

const addUsersUrl = computed(() => `${baseUrl}/MF/cs-mf-addusers/?layout=none&locationid=${locationId.value}`)
const manageUsersUrl = computed(() => `/users?layout=none`)
const manageSkillsUrl = computed(() => `/skills/${locationId.value}?layout=none`)

async function loadInitialData() {
  if (!locationId.value) return
  existingSkills.value = await fetchExistingSkills(locationId.value)
  users.value = normalizeUsers(await fetchLocationUsers(locationId.value))
  const context = await fetchLocationSignUpContext(locationId.value)
  const parsed = parseSignUpContext(context)

  if (parsed.events && parsed.events !== 'N/A') {
    // Use first event/service if multiple are entered
    activity.value.name = parsed.events.split(/[,;\n]/)[0].trim()
  }
  if (parsed.groups?.length) {
    workgroups.value = parsed.groups.map(g => ({
      name: g.name,
      count: g.count || 1,
      autoSchedule: true,
    }))
  }
}

function reuseSimilarActivity(candidate) {
  const activityId = candidate.activityid || candidate.id
  if (!activityId) {
    toast.error('The selected Activity does not have a valid ID.', { position: 'top-right' })
    return
  }
  return createBaseSetup({ reuseActivityId: activityId })
}

function createSeparateActivity() {
  const acknowledgedSimilarIds = activityMatchCandidates.value
    .map(candidate => candidate.activityid || candidate.id)
    .filter(Boolean)
  return createBaseSetup({ allowSimilarCreation: true, acknowledgedSimilarIds })
}

function clearActivityMatchReview() {
  activityMatchCandidates.value = []
  activityMatchQuery.value = ''
}

function normalizeUsers(rawUsers) {
  return (rawUsers || []).map(u => ({
    personId: u.personId || u.personid || u.person_id,
    firstName: u.firstName || u.firstname || '',
    lastName: u.lastName || u.lastname || '',
    email: u.email || u.emailAddress || '',
  })).filter(u => u.personId)
}

async function createBaseSetup({
  reuseActivityId = null,
  allowSimilarCreation = false,
  acknowledgedSimilarIds = [],
} = {}) {
  if (!isWorkgroupsValid.value) {
    toast.error('Please enter the Activity and at least one Workgroup.', { position: 'top-right' })
    return
  }
  if (!locationId.value) {
    toast.error('No location selected.', { position: 'top-right' })
    return
  }
  if (!reuseActivityId && !allowSimilarCreation) {
    clearActivityMatchReview()
  }

  saving.value = true
  try {
    if (!skillReviewConfirmed.value) {
      const review = await findSkillMatchesToReview()
      if (review.length > 0) {
        skillMatchReview.value = review
        skillChoices.value = Object.fromEntries(
          review.map(item => [item.normKey, item.candidates[0].id])
        )
        return
      }
    }
    const existingActivities = await fetchExistingActivities(locationId.value)
    const normalizedName = normalizeActivityName(activity.value.name)
    const existingAct = existingActivities.find(
      candidate => normalizeActivityName(candidate.activityname || candidate.name || '') === normalizedName
    )
    let activityId
    if (existingAct) {
      activityId = existingAct.activityid || existingAct.id
      toast.info(`Activity "${activity.value.name}" already exists — reusing it.`, { position: 'top-right' })
    } else if (reuseActivityId) {
      const selectedActivity = existingActivities.find(
        candidate => String(candidate.activityid || candidate.id) === String(reuseActivityId)
      )
      if (!selectedActivity) {
        throw new Error('The selected Activity is no longer available. Please review the Activity matches again.')
      }
      activityId = selectedActivity.activityid || selectedActivity.id
      toast.info(`Reusing Activity "${selectedActivity.activityname || selectedActivity.name}".`, { position: 'top-right' })
    } else {
      const similarActivities = findPotentialActivityMatches(activity.value.name, existingActivities)
      const acknowledgedIds = new Set(acknowledgedSimilarIds.map(id => String(id)))
      const unreviewedMatches = similarActivities.filter(
        candidate => !acknowledgedIds.has(String(candidate.activityid || candidate.id))
      )
      if (unreviewedMatches.length > 0 || (similarActivities.length > 0 && !allowSimilarCreation)) {
        activityMatchCandidates.value = similarActivities
        activityMatchQuery.value = normalizedName
        return
      }
      const activityResponse = await createActivity(locationId.value, activity.value)
      activityId = activityResponse.id || activityResponse.output?.id || activityResponse.activityid
      if (!activityId) throw new Error('Activity was created, but no activity id was returned.')
    }
    createdActivityId.value = activityId
    clearActivityMatchReview()

    // ── Create/reuse skills (with fuzzy dedup) ──
    const skillIdByNormKey = await ensureSkills()

    // ── Fetch existing workgroups for this activity ──
    const existingWgs = await fetchActivityWorkgroups(createdActivityId.value)
    const existingWgNorms = existingWgs.map(w => normalizeForComparison(w.name || w.workgroupname || ''))

    // ── Create workgroups (skip duplicates) ──
    const created = []
    for (const wg of workgroups.value.filter(w => w.name?.trim())) {
      const wgNorm = normalizeForComparison(wg.name)
      // Check if this workgroup already exists for the activity
      const alreadyExists = existingWgNorms.includes(wgNorm)
      if (alreadyExists) {
        const existing = existingWgs.find(w => normalizeForComparison(w.name || w.workgroupname || '') === wgNorm)
        created.push({
          id: existing.workgroupid || existing.id,
          name: existing.name || existing.workgroupname,
          personsNeeded: wg.count || 1,
          skillId: existing.skillid,
        })
        continue
      }

      const derived = deriveSkillName(wg.name)
      const normKey = normalizeForComparison(derived)
      const skillId = skillIdByNormKey[normKey]
      const result = await createWorkgroup(createdActivityId.value, {
        name: wg.name.trim(),
        skillId,
        personsNeeded: wg.count || 1,
        autoSchedule: wg.autoSchedule,
        adminEmail: session.value.emailAddress || session.value.email || '',
      })
      created.push({
        id: result.id || result.workgroupid || result.output?.id,
        name: wg.name.trim(),
        personsNeeded: wg.count || 1,
        skillId,
      })
    }
    createdWorkgroups.value = created.filter(w => w.id)

    // ── Create recurrence with user-selected start date ──
    const recurrenceParams = mapRecurrenceToApi(
      activity.value.recurrence, createdActivityId.value, activity.value.recurStartDate
    )
    if (recurrenceParams) {
      await createRecurrence(locationId.value, recurrenceParams)
    }

    setupCreated.value = true
    skillReviewConfirmed.value = false
    users.value = normalizeUsers(await fetchLocationUsers(locationId.value))
    await loadSkillAssignments()
    toast.success('Activity, skills, and workgroups created.', { position: 'top-right' })
  } catch (err) {
    toast.error(err.message || 'Failed to create setup.', { position: 'top-right' })
  } finally {
    saving.value = false
  }
}

/**
 * Ensure skills exist, using fuzzy matching to reuse existing ones.
 * Returns { normalisedKey: skillId }
 */
async function ensureSkills() {
  const skillIdByNormKey = {}
  // Refresh existing skills from server
  existingSkills.value = await fetchExistingSkills(locationId.value)
  const existing = existingSkills.value || []

  // Index existing skills by normalised key
  for (const skill of existing) {
    const name = skill.skillname || skill.name || ''
    const id = skill.skillid || skill.id
    const normKey = normalizeForComparison(name)
    if (normKey && id) skillIdByNormKey[normKey] = id
  }

  for (const skillDisplayName of generatedSkills.value) {
    const normKey = normalizeForComparison(skillDisplayName)
    if (skillIdByNormKey[normKey]) continue // Already have a match

    // Check for fuzzy match against current existing skills
    const choice = skillChoices.value[normKey]
    if (choice && choice !== 'new') {
      skillIdByNormKey[normKey] = choice
      continue
    }
    const match = choice === 'new' ? null : findMatchingSkill(skillDisplayName, existing)
    if (match) {
      const matchId = match.skillid || match.id
      skillIdByNormKey[normKey] = matchId
    } else {
      const created = await createSkill(locationId.value, skillDisplayName, 'Created by Quick Setup from workgroup names')
      const newId = created.id || created.skillid || created.output?.id
      skillIdByNormKey[normKey] = newId
      // Add to existing list so subsequent iterations can match against it
      existing.push({ skillname: skillDisplayName, skillid: newId })
    }
  }
  createdSkillByName.value = skillIdByNormKey
  return skillIdByNormKey
}

// Skills (without an exact match) that resemble an existing skill, so the user can reuse it.
async function findSkillMatchesToReview() {
  const current = await fetchExistingSkills(locationId.value)
  existingSkills.value = current
  const exactKeys = new Set(
    (current || []).map(s => normalizeForComparison(s.skillname || s.name || '')).filter(Boolean)
  )
  const review = []
  for (const name of generatedSkills.value) {
    const normKey = normalizeForComparison(name)
    if (!normKey || exactKeys.has(normKey) || findMatchingSkill(name, current)) continue
    const candidates = findSimilarSkills(name, current)
      .map(s => ({ id: s.skillid || s.id, name: s.skillname || s.name }))
      .filter(c => c.id && c.name)
    if (candidates.length) review.push({ normKey, name, candidates })
  }
  return review
}

function confirmSkillMatches() {
  skillReviewConfirmed.value = true
  skillMatchReview.value = []
  return createBaseSetup()
}

function clearSkillMatchReview() {
  skillMatchReview.value = []
  skillChoices.value = {}
  skillReviewConfirmed.value = false
}

async function loadSkillAssignments() {
  if (!createdActivityId.value) return
  skillAssignmentsLoading.value = true
  try {
    const [assignments, eligibility] = await Promise.all([
      fetchQuickSetupSkillAssignments(createdActivityId.value),
      fetchQuickSetupSkillEligibility(createdActivityId.value).catch(() => null),
    ])
    skillAssignments.value = assignments
    eligibilityLoaded.value = Array.isArray(eligibility)
    skillEligibility.value = eligibilityLoaded.value ? eligibility : []
  } catch {
    toast.error('Could not load the skill assignments. Use Refresh to try again.', { position: 'top-right' })
  } finally {
    skillAssignmentsLoading.value = false
  }
}

async function addAllBlockedAsPreferred() {
  if (addingPreferred.value || !createdActivityId.value) return
  addingPreferred.value = true
  try {
    const result = await addQuickSetupPreferredPeople(createdActivityId.value)
    skillEligibility.value = result.skills || skillEligibility.value
    toast.success(`${result.added_count || 0} people can now be scheduled for this Activity.`, { position: 'top-right' })
  } catch (err) {
    toast.error(err.response?.data?.message || 'Could not update the preferred users.', { position: 'top-right' })
  } finally {
    addingPreferred.value = false
  }
}

function openPreferredModal() {
  showPreferredModal.value = true
}

async function closePreferredModal() {
  showPreferredModal.value = false
  await loadSkillAssignments()
}

async function refreshPeopleData() {
  users.value = normalizeUsers(await fetchLocationUsers(locationId.value))
  await loadSkillAssignments()
}

function openAddUsersModal() {
  showAddUsersModal.value = true
}

async function closeAddUsersModal() {
  showAddUsersModal.value = false
  await refreshPeopleData()
}

function openManageUsersModal() {
  showManageUsersModal.value = true
}

async function closeManageUsersModal() {
  showManageUsersModal.value = false
  await refreshPeopleData()
}

function openManageSkillsModal() {
  showManageSkillsModal.value = true
}

async function closeManageSkillsModal() {
  showManageSkillsModal.value = false
  await refreshPeopleData()
}

function goToManageSchedules() {
  router.push(manageSchedulesPath.value)
}

async function runEngineNow() {
  if (engineStatus.value === 'running' || !createdActivityId.value) return
  engineStatus.value = 'running'
  engineError.value = ''
  engineStep.value = 1
  engineInstancesTotal.value = 0
  engineInstancesScheduled.value = 0
  enginePersonsAssigned.value = 0
  engineCurrentLabel.value = 'Creating your schedule instances...'
  try {
    const created = await runQuickSetupEngine(createdActivityId.value, 'instances')
    const instances = created.instances || []
    engineInstancesTotal.value = instances.length
    engineStep.value = 2
    for (const instance of instances) {
      engineCurrentLabel.value = `Scheduling people for ${formatInstanceLabel(instance.start)}...`
      const result = await runQuickSetupEngine(createdActivityId.value, 'schedule', instance.id)
      enginePersonsAssigned.value += Number(result.persons_assigned || 0)
      engineInstancesScheduled.value += 1
    }
    engineStep.value = 3
    engineCurrentLabel.value = ''
    scheduleProgress.value = { ...scheduleProgress.value, upcoming_instance_count: instances.length }
    engineStatus.value = 'done'
    toast.success('Your schedule is ready.', { position: 'top-right' })
  } catch (err) {
    engineStatus.value = 'error'
    engineError.value = err.response?.data?.message || err.message || 'The scheduling engine could not finish.'
  }
}

function formatInstanceLabel(start) {
  if (!start) return 'an upcoming event'
  const date = new Date(String(start).replace(' ', 'T'))
  return Number.isNaN(date.getTime())
    ? String(start)
    : date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

async function finishSetup() {
  if (!skillsHavePeople.value) {
    toast.error('Assign at least one person to a skill before finishing.', { position: 'top-right' })
    return
  }
  finishing.value = true
  try {
    const activityAdminIds = (adminSelection.value.activityAdmins || []).map(a => a.id)
    if (activityAdminIds.length) {
      await saveActivityAdmins(createdActivityId.value, activityAdminIds)
    }
    for (const [wgId, admins] of Object.entries(adminSelection.value.workgroupAdmins || {})) {
      const wgAdminIds = (admins || []).map(a => a.id)
      if (wgAdminIds.length) {
        await saveWorkgroupAdmins(wgId, wgAdminIds)
      }
    }
    await enableActivity(createdActivityId.value)
    completed.value = true
    toast.success('Quick Setup complete. Your Activity is enabled.', { position: 'top-right' })
    void sendCompletionEmail()
    void refreshScheduleProgress()
  } catch (err) {
    toast.error(err.message || 'Failed to complete setup.', { position: 'top-right' })
  } finally {
    finishing.value = false
  }
}

async function sendCompletionEmail() {
  if (!createdActivityId.value || !locationId.value) return

  completionEmailStatus.value = 'sending'
  completionEmailError.value = ''
  try {
    await sendQuickSetupCompletionEmail(createdActivityId.value, locationId.value)
    completionEmailStatus.value = 'sent'
  } catch (err) {
    completionEmailStatus.value = 'error'
    completionEmailError.value = err.response?.data?.message || err.message || 'Use Retry email to try again.'
    toast.error('Setup is complete, but the summary email could not be sent.', { position: 'top-right' })
  }
}

async function refreshScheduleProgress() {
  if (!createdActivityId.value || scheduleProgressRequestInFlight) return
  if (scheduleProgressTimer) clearTimeout(scheduleProgressTimer)

  scheduleProgressRequestInFlight = true
  scheduleProgressStatus.value = 'checking'
  scheduleProgressCheckCount += 1
  try {
    scheduleProgress.value = await fetchQuickSetupScheduleProgress(createdActivityId.value)
    if (scheduleProgress.value.status === 'ready') {
      scheduleProgressStatus.value = 'ready'
      return
    }
    if (scheduleProgress.value.status !== 'waiting') {
      scheduleProgressStatus.value = scheduleProgress.value.status || 'error'
      return
    }
    if (scheduleProgressCheckCount >= MAX_PROGRESS_CHECKS) {
      scheduleProgressStatus.value = 'timed_out'
      return
    }

    scheduleProgressStatus.value = 'waiting'
    scheduleProgressTimer = setTimeout(refreshScheduleProgress, PROGRESS_CHECK_INTERVAL_MS)
  } catch {
    scheduleProgressStatus.value = 'error'
  } finally {
    scheduleProgressRequestInFlight = false
  }
}

onMounted(loadInitialData)
onUnmounted(() => {
  if (scheduleProgressTimer) clearTimeout(scheduleProgressTimer)
})
</script>

<style scoped>
.qs-heading {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.qs-heading__icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
}
.qs-heading__icon i { font-size: 28px; }
.qs-heading__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #344767;
}
.qs-heading__subtitle {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: #7b809a;
}
.text-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.qs-progress-pill {
  background: #f5f7ff;
  color: #667eea;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.45rem 0.8rem;
}
.qs-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.qs-section {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f0f2f5;
}
.qs-section:last-child { border-bottom: 0; }
.qs-section--locked {
  opacity: 0.55;
  pointer-events: none;
}
.qs-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.qs-step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  margin-right: 0.5rem;
  color: #fff;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-size: 0.78rem;
  font-weight: 700;
}
.qs-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
}
.qs-summary p {
  margin: 0 0 0.25rem;
  font-size: 0.86rem;
}
.qs-generation-status {
  border: 1px solid #dce5df;
  border-radius: 8px;
  padding: 1rem;
  background: #f8faf8;
}
.qs-generation-status__title { margin: 0 0 0.75rem; font-size: 0.95rem; font-weight: 700; }
.qs-generation-status__steps { display: grid; gap: 0.8rem; margin: 0; padding: 0; list-style: none; }
.qs-generation-status__step { display: flex; align-items: flex-start; gap: 0.65rem; }
.qs-generation-status__step > i { color: #6c757d; font-size: 20px; line-height: 1.2; }
.qs-generation-status__step--done > i { color: #198754; }
.qs-generation-status__step--waiting > i { color: #b07b19; }
.qs-generation-status__step--attention > i { color: #b54730; }
.qs-generation-status__step div { display: grid; gap: 0.15rem; }
.qs-generation-status__step span { color: #68756d; font-size: 0.82rem; line-height: 1.45; }
.qs-generation-status__email {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid #dce5df;
  font-size: 0.82rem;
}
.qs-chat-wrap {
  height: calc(100vh - 210px);
  min-height: 620px;
  position: sticky;
  top: 1rem;
}
@media (max-width: 991px) {
  .qs-chat-wrap {
    height: 520px;
    min-height: 520px;
    position: static;
  }
}

/* Pulse animation to draw attention to the next required action */
.qs-pulse {
  animation: qs-pulse 1.5s ease-in-out infinite;
}
@keyframes qs-pulse {
  0% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.55); }
  70% { box-shadow: 0 0 0 12px rgba(102, 126, 234, 0); }
  100% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0); }
}
</style>
